<template>
  <div>
    <Head title="Create Channel" />
    <h1 class="mb-8 text-3xl font-bold">
      <Link class="text-blue-400 hover:text-blue-600" href="/channels">Channels</Link>
      <span class="text-blue-400 font-medium">/</span>Create
    </h1>
    <div class="max-w-3xl bg-white  shadow overflow-hidden">
      <form @submit.prevent="store">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <select-input v-model="form.system" :error="form.errors.system" class="pb-8 pr-6 w-full lg:w-1/3" label="System">
            <option v-for="s in systems" :key="s.id" :value="s.id"> {{ s.name }}</option>
          </select-input>
          <select-input v-model="form.width" :error="form.errors.width" class="pb-8 pr-6 w-full lg:w-1/3" label="Width">
            <option v-for="w in widths" :key="w.id" :value="w.id"> {{ w.label }}</option>
          </select-input>
          <select-input v-model="form.edge" :error="form.errors.edge" class="pb-8 pr-6 w-full lg:w-1/3" label="Edge">
            <option v-for="e in edges" :key="e.id" :value="e.id"> {{ e.name }}</option>
          </select-input>
          <text-input v-model="form.title" :error="form.errors.title" class="pb-8 pr-6 w-full lg:w-1/2" label="Title" />
          <text-input v-model="form.part_number" :error="form.errors.part_number" class="pb-8 pr-6 w-full lg:w-1/2" label="Part number" />
          <text-input v-model="form.length_im" :error="form.errors.length_im" type="number" min="0" class="pb-8 pr-6 w-full lg:w-1/2" label="Length (in)" step="0.01" />
          <text-input v-model="form.length" :error="form.errors.length" type="number" min="0" class="pb-8 pr-6 w-full lg:w-1/2" label="Length (m)" />
          <text-input v-model="form.depth_im" :error="form.errors.depth_im" type="number" min="0" class="pb-8 pr-6 w-full lg:w-1/2" label="Depth (in)" step="0.01" />
          <text-input v-model="form.depth" :error="form.errors.depth" type="number" min="0" class="pb-8 pr-6 w-full lg:w-1/2" label="Depth (m)" />
          <text-input v-model="form.bo_size" :error="form.errors.bo_size" type="number" min="0" class="pb-8 pr-6 w-full lg:w-1/2" label="Bottom Outlet Size" />
          <div class="w-full flex">
            <div class="pb-8 pr-6 w-full lg:w-1/2 flex items-center">
              <p class="pr-6">Shallow</p>
              <checkbox-input v-model="form.shallow" :error="form.errors.shallow" />
            </div>
            <div class="pb-8 pr-6 w-full lg:w-1/2 flex items-center">
              <p class="pr-6">Neutral</p>
              <checkbox-input v-model="form.neutral" :error="form.errors.neutral" />
            </div>
            <div class="pb-8 pr-6 w-full lg:w-1/2 flex items-center">
              <p class="pr-6">Directional Change</p>
              <checkbox-input v-model="form.direction_change" :error="form.errors.direction_change" />
            </div>
          </div>
          <textarea-input v-model="form.description" :error="form.errors.description" class="pb-8 pr-6 w-full" label="Description" />
          <!-- <div class="w-full flex justify-end">
            <text-input v-model="form.price" :error="form.errors.price" class="pb-8 pr-6 w-full lg:w-1/2" label="Price" type="number" min="0" step="0.01" />
          </div> -->
        </div>
        <!-- Price Lists -->
        <div class="mt-6 rounded shadow overflow-x-auto">
          <table class="w-full whitespace-nowrap">
            <tr class="text-left font-bold">
              <th class="pb-4 pt-6 px-6">PriceLists</th>
              <th class="pb-4 pt-6 px-6">Price</th>
            </tr>
            <tr v-for="(pricelist, i) in form.pricelists" :key="pricelist.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
              <td class="px-6 py-2 focus:text-blue-500">
                {{ pricelist.name }}
                <icon v-if="pricelist.deleted_at" name="trash" class="flex-shrink-0 ml-2 w-3 h-3 fill-gray-400" />
              </td>
              <td class="px-6 py-2 focus:text-blue-500">
                <text-input v-model="pricelist.price" :error="form.errors[`pricelists.${i}.price`]" class="w-full" label="" type="number" min="0" step="0.01" />
              </td>
            </tr>
            <tr v-if="pricelists.length === 0">
              <td class="px-6 py-4 border-t" colspan="4">No price lists found.</td>
            </tr>
          </table>
        </div>

        <div class="flex items-center justify-end px-8 py-4 bg-gray-50 border-t border-gray-100">
          <loading-button :loading="form.processing" class="btn-blue" type="submit">Create Channel</loading-button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import SelectInput from '@/Shared/SelectInput'
import TextInput from '@/Shared/TextInput'
import TextareaInput from '@/Shared/TextareaInput'
import CheckboxInput from '@/Shared/CheckboxInput'
import LoadingButton from '@/Shared/LoadingButton'

export default {
  components: {
    Head,
    Link,
    Icon,
    LoadingButton,
    SelectInput,
    TextInput,
    TextareaInput,
    CheckboxInput,
  },
  layout: Layout,
  props: {
    systems: Object,
    widths : Object,
    edges  : Object,
    channel: Object,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        system          : null,
        width           : null,
        edge            : null,
        title           : null,
        part_number     : null,
        length_im       : null,
        length          : null,
        depth_im        : null,
        depth           : null,
        bo_size         : null,
        shallow         : null,
        neutral         : null,
        direction_change: null,
        description     : null,
        price           : null,
      }),
    }
  },
  methods: {
    store() {
      this.form.post('/channels')
    },
  },
}
</script>
